import { C } from '../data/constants.js';
import { LEVELS } from '../data/levels.js';
import { Paddle } from '../entities/Paddle.js';
import { Ball } from '../entities/Ball.js';
import { Brick } from '../entities/Brick.js';
import { PowerUp, PU_TYPES } from '../entities/PowerUp.js';
import { Projectile } from '../entities/Projectile.js';
import { Physics } from '../systems/Physics.js';
import { ParticleSystem } from '../systems/ParticleSystem.js';
import { ScoreManager } from '../systems/ScoreManager.js';
import { AudioManager } from '../systems/AudioManager.js';
import { PowerUpManager } from '../systems/PowerUpManager.js';
import { Camera } from './Camera.js';
import { createBoss } from '../entities/Boss.js';
import { VERSION } from '../data/version.js';
import { ProfileManager } from '../systems/ProfileManager.js';
import { LeaderboardManager } from '../systems/LeaderboardManager.js';

// Game states
const STATE = {
  TITLE: 'TITLE',
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  LEVEL_CLEAR: 'LEVEL_CLEAR',
  BOSS_INTRO: 'BOSS_INTRO',
  BOSS_FIGHT: 'BOSS_FIGHT',
  BOSS_VICTORY: 'BOSS_VICTORY',
  VICTORY: 'VICTORY',
  GAME_OVER: 'GAME_OVER',
};

const SHARE_URL = 'http://bricks-yo.mindlinkstudios.com/';

export class Game {
  constructor() {
    this.state = STATE.TITLE;
    this.camera = new Camera();
    this.particles = new ParticleSystem();
    this.score = new ScoreManager();
    this.audio = new AudioManager();
    this.powerUpManager = new PowerUpManager();
    this.profile = new ProfileManager();
    this.leaderboard = new LeaderboardManager();

    this.paddle = null;
    this.balls = [];          // multi-ball support
    this.bricks = [];
    this.powerUps = [];       // falling capsules
    this.projectiles = [];    // laser shots
    this.currentLevel = 0;

    // Level & Boss timers
    this.stateTimer = 0;
    this.boss = null;
    this.bossIntroTimer = 0;

    // Title screen animation
    this.titlePulse = 0;

    // Laser fire cooldown (ticks)
    this.laserCooldown = 0;

    // EMP shockwave animation
    this.empWave = null; // { x, y, radius, maxRadius, alpha }

    // Share & Toast notification
    this.toastMessage = '';
    this.toastTimer = 0;
    this.shareBtnBounds = null;

    // Guide & Privacy Info Modal
    this.showInfoModal = false;
    this.modalTab = 'powerups'; // 'powerups' | 'sound' | 'privacy'
    this.modalCooldown = 0;
    this.clickedBtn = null; // { id: string, timer: number }

    // Background animation clock
    this.bgTime = 0;

    // CGI / Perl Hit Counter
    this.loadCount = null;
    this._fetchHitCounter();
  }

  _fetchHitCounter() {
    fetch('/cgi-bin/counter.pl')
      .then(res => res.json())
      .then(data => {
        if (data && typeof data.count === 'number') {
          this.loadCount = data.count;
        }
      })
      .catch(() => {
        // Fallback for local development
        try {
          let count = parseInt(localStorage.getItem('bricksYoHitCount') || '1024', 10);
          count++;
          localStorage.setItem('bricksYoHitCount', String(count));
          this.loadCount = count;
        } catch {
          this.loadCount = 1024;
        }
      });
  }

  // --- State transitions ---

  startGame() {
    this.audio.init();
    this.score.reset();
    this.powerUpManager.reset();
    this.currentLevel = 0;
    this.loadLevel(this.currentLevel);
  }

  loadLevel(index) {
    const levelData = LEVELS[index % LEVELS.length];

    // Create paddle & attach initial ball
    this.paddle = new Paddle();
    this.balls = [new Ball()];
    this.balls[0].attachTo(this.paddle);

    // Reset temporary items
    this.powerUps = [];
    this.projectiles = [];
    this.powerUpManager.reset();
    this.empWave = null;
    this.laserCooldown = 0;
    this.boss = null;

    if (levelData.boss) {
      // Initialize sector boss
      this.bricks = [];
      this.boss = createBoss(levelData.sector);
      this.state = STATE.BOSS_INTRO;
      this.bossIntroTimer = 2200; // ms
      this.camera.shake(8, 600);
      this.camera.triggerChromaticAberration(6, 400);
      this.camera.triggerFlash(0.3);
      this.audio.playTrack('boss');
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
    } else {
      // Create bricks from level grid
      this.bricks = [];
      for (let row = 0; row < levelData.grid.length; row++) {
        for (let col = 0; col < levelData.grid[row].length; col++) {
          const type = levelData.grid[row][col];
          if (type !== C.BRICK_EMPTY) {
            this.bricks.push(new Brick(col, row, type));
          }
        }
      }
      this.state = STATE.PLAYING;
      this.audio.playTrack(`sector${levelData.sector}`);
    }
  }

  nextLevel() {
    this.currentLevel++;
    if (this.currentLevel >= LEVELS.length) {
      // Beat all 20 levels!
      this.state = STATE.VICTORY;
      this.audio.levelClear();
      const levelName = LEVELS[(this.currentLevel - 1) % LEVELS.length].name;
      this.leaderboard.submitScore(this.profile.username, this.score.score, `SECTOR ${levelName}`)
        .then(res => {
          if (res && res.isTop20 && res.rank) {
            this.toastMessage = `★ TOP 20 RECORD: RANK #${res.rank}! ★`;
            this.toastTimer = 4000;
          }
        });
      return;
    }
    this.loadLevel(this.currentLevel);
  }

  // --- Update ---

  update(input) {
    const dtMs = C.TICK_RATE * 1000;

    this.bgTime += 0.015;
    this.camera.update(dtMs);
    this.particles.update();

    if (this.toastTimer > 0) {
      this.toastTimer -= dtMs;
      if (this.toastTimer <= 0) this.toastMessage = '';
    }

    if (this.modalCooldown > 0) {
      this.modalCooldown -= dtMs;
    }

    if (this.clickedBtn) {
      this.clickedBtn.timer -= dtMs;
      if (this.clickedBtn.timer <= 0) {
        this.clickedBtn = null;
      }
    }

    switch (this.state) {
      case STATE.TITLE:
        this.updateTitle(input, dtMs);
        break;
      case STATE.PLAYING:
        this.updatePlaying(input, dtMs);
        break;
      case STATE.PAUSED:
        this.updatePaused(input);
        break;
      case STATE.LEVEL_CLEAR:
        this.updateLevelClear(dtMs);
        break;
      case STATE.BOSS_INTRO:
        this.updateBossIntro(input, dtMs);
        break;
      case STATE.BOSS_FIGHT:
        this.updateBossFight(input, dtMs);
        break;
      case STATE.BOSS_VICTORY:
        this.updateBossVictory(dtMs);
        break;
      case STATE.VICTORY:
        this.updateVictory(input);
        break;
      case STATE.GAME_OVER:
        this.updateGameOver(input);
        break;
    }

    // Mute toggle (Key M)
    if (input.keyJustPressed && input.keyJustPressed['KeyM']) {
      this.audio.toggleMute();
    }
  }

  updateTitle(input, dtMs) {
    this.titlePulse += 0.03;

    if (input.tapped) {
      this.audio.init();

      const mw = 370;
      const mh = 570;
      const mx = (C.CANVAS_W - mw) / 2;
      const my = (C.CANVAS_H - mh) / 2;
      const tapX = input.tapX;
      const tapY = input.tapY;

      // If modal is open, handle tab switching, sound toggles, or close
      if (this.showInfoModal) {
        if (this.modalCooldown > 0) return;

        // 1. Tab 1: Power-Ups (x: mx+5 to mx+90, y: my+5 to my+52)
        if (tapX >= mx + 5 && tapX <= mx + 90 && tapY >= my + 5 && tapY <= my + 52) {
          this.modalTab = 'powerups';
          this.clickedBtn = { id: 'tab1', timer: 160 };
          this.audio.buttonClick();
          return;
        }

        // 2. Tab 2: Leaderboard (x: mx+92 to mx+178, y: my+5 to my+52)
        if (tapX >= mx + 92 && tapX <= mx + 178 && tapY >= my + 5 && tapY <= my + 52) {
          this.modalTab = 'leaderboard';
          this.clickedBtn = { id: 'tab2', timer: 160 };
          this.leaderboard.fetchScores();
          this.audio.buttonClick();
          return;
        }

        // 3. Tab 3: Sound Settings (x: mx+180 to mx+266, y: my+5 to my+52)
        if (tapX >= mx + 180 && tapX <= mx + 266 && tapY >= my + 5 && tapY <= my + 52) {
          this.modalTab = 'sound';
          this.clickedBtn = { id: 'tab3', timer: 160 };
          this.audio.buttonClick();
          return;
        }

        // 4. Tab 4: Privacy (x: mx+268 to mx+mw-5, y: my+5 to my+52)
        if (tapX >= mx + 268 && tapX <= mx + mw - 5 && tapY >= my + 5 && tapY <= my + 52) {
          this.modalTab = 'privacy';
          this.clickedBtn = { id: 'tab4', timer: 160 };
          this.audio.buttonClick();
          return;
        }

        // Inside Leaderboard Tab: Refresh Button
        if (this.modalTab === 'leaderboard') {
          const rfX = (C.CANVAS_W - 190) / 2;
          const rfY = my + mh - 92;
          if (tapX >= rfX && tapX <= rfX + 190 && tapY >= rfY && tapY <= rfY + 30) {
            this.leaderboard.fetchScores();
            this.clickedBtn = { id: 'btn_refresh', timer: 200 };
            this.audio.buttonClick();
            this.toastMessage = 'LEADERBOARD REFRESHED! 🏆';
            this.toastTimer = 1800;
            return;
          }
        }

        // Sound Settings interactive toggles (when Sound tab is active)
        if (this.modalTab === 'sound') {
          const optBtnX = mx + 20;
          const optBtnW = mw - 40;

          // Music Toggle (my+95 to my+155)
          if (tapX >= optBtnX && tapX <= optBtnX + optBtnW && tapY >= my + 95 && tapY <= my + 155) {
            this.audio.toggleMusic();
            this.clickedBtn = { id: 'opt_music', timer: 160 };
            this.audio.buttonClick();
            return;
          }
          // SFX Toggle (my+155 to my+215)
          if (tapX >= optBtnX && tapX <= optBtnX + optBtnW && tapY >= my + 155 && tapY <= my + 215) {
            this.audio.toggleSFX();
            this.clickedBtn = { id: 'opt_sfx', timer: 160 };
            this.audio.buttonClick();
            return;
          }
          // Master Mute Toggle (my+215 to my+275)
          if (tapX >= optBtnX && tapX <= optBtnX + optBtnW && tapY >= my + 215 && tapY <= my + 275) {
            this.audio.toggleMute();
            this.clickedBtn = { id: 'opt_mute', timer: 160 };
            this.audio.buttonClick();
            return;
          }
        }

        // 5. Close Button (bottom bar)
        const closeBtnX = (C.CANVAS_W - 280) / 2;
        const closeBtnY = my + mh - 58;
        const isCloseBtn = (tapX >= closeBtnX - 10 && tapX <= closeBtnX + 280 + 10 && tapY >= closeBtnY - 6 && tapY <= closeBtnY + 50);

        // 6. Top-Right [✕] Button
        const isTopRightClose = (tapX >= mx + mw - 40 && tapX <= mx + mw + 15 && tapY >= my - 15 && tapY <= my + 45);

        // 7. Backdrop Tap (outside card)
        const isOutside = (tapX < mx || tapX > mx + mw || tapY < my || tapY > my + mh);

        if (isCloseBtn || isTopRightClose || isOutside) {
          this.showInfoModal = false;
          this.clickedBtn = { id: 'btn_close', timer: 160 };
          this.audio.buttonClick();
          return;
        }

        // Tap inside modal body content does nothing (keeps modal open)
        return;
      }

      // ───────────────────────────────────────────────────────────────────────
      // Title Screen Buttons (Callsign Reroll, Power-Ups, Leaderboard, Sound, Privacy)
      // ───────────────────────────────────────────────────────────────────────
      const px = 50;
      const pw = 300;

      // Pilot Callsign Badge (tap to re-roll callsign)
      if (tapX >= 60 && tapX <= 340 && tapY >= 440 && tapY <= 480) {
        this.profile.rerollUsername();
        this.clickedBtn = { id: 'btn_reroll', timer: 200 };
        this.audio.buttonClick();
        this.toastMessage = `CALLSIGN: ${this.profile.username} 🎲`;
        this.toastTimer = 2200;
        return;
      }

      // Button 1: Power-Ups Guide (y: 495 to 528)
      if (tapX >= px && tapX <= px + pw && tapY >= 495 && tapY < 528) {
        this.showInfoModal = true;
        this.modalTab = 'powerups';
        this.modalCooldown = 150;
        this.clickedBtn = { id: 'btn_powerups', timer: 200 };
        this.audio.buttonClick();
        return;
      }

      // Button 2: Top 20 Leaderboard (y: 530 to 563)
      if (tapX >= px && tapX <= px + pw && tapY >= 530 && tapY < 563) {
        this.showInfoModal = true;
        this.modalTab = 'leaderboard';
        this.leaderboard.fetchScores();
        this.modalCooldown = 150;
        this.clickedBtn = { id: 'btn_leaderboard', timer: 200 };
        this.audio.buttonClick();
        return;
      }

      // Button 3: Sound Settings (y: 565 to 598)
      if (tapX >= px && tapX <= px + pw && tapY >= 565 && tapY < 598) {
        this.showInfoModal = true;
        this.modalTab = 'sound';
        this.modalCooldown = 150;
        this.clickedBtn = { id: 'btn_sound', timer: 200 };
        this.audio.buttonClick();
        return;
      }

      // Button 4: Privacy & Warnings (y: 600 to 635)
      if (tapX >= px && tapX <= px + pw && tapY >= 600 && tapY <= 635) {
        this.showInfoModal = true;
        this.modalTab = 'privacy';
        this.modalCooldown = 150;
        this.clickedBtn = { id: 'btn_privacy', timer: 200 };
        this.audio.buttonClick();
        return;
      }

      // Only start game if tapping the upper play area
      if (tapY < 440) {
        this.startGame();
      }
    } else if (input.keyJustPressed && (input.keyJustPressed['Space'] || input.keyJustPressed['Enter'])) {
      if (this.showInfoModal) {
        this.showInfoModal = false;
      } else {
        this.startGame();
      }
    }
  }

  updatePlaying(input, dtMs) {
    if (input.pausePressed) {
      this.state = STATE.PAUSED;
      return;
    }

    this.paddle.update(input);
    this.paddle.w = C.PADDLE_W * this.powerUpManager.paddleWidthMultiplier;

    for (const brick of this.bricks) {
      brick.update(dtMs);
    }

    this.powerUpManager.update(dtMs);

    if (this.laserCooldown > 0) this.laserCooldown--;
    if (this.powerUpManager.laserActive && input.launchPressed && this.laserCooldown <= 0) {
      const anyAttached = this.balls.some(b => b.attached);
      if (!anyAttached) {
        this.projectiles.push(new Projectile(this.paddle.x - 10, this.paddle.top));
        this.projectiles.push(new Projectile(this.paddle.x + 10, this.paddle.top));
        this.laserCooldown = 10;
        this.audio.laserShot();
      }
    }

    let launchedThisFrame = false;
    for (const ball of this.balls) {
      if (ball.attached) {
        ball.attachTo(this.paddle);
        if (input.launchPressed && !launchedThisFrame) {
          ball.launch(this.paddle);
          launchedThisFrame = true;
        }
        continue;
      }

      ball.update();

      if (Physics.ballPaddle(ball, this.paddle)) {
        if (this.powerUpManager.magnetCatches > 0) {
          ball.attachTo(this.paddle);
          this.powerUpManager.useMagnetCatch();
        }
        this.audio.paddleHit((ball.x - this.paddle.x) / (this.paddle.w / 2));
        if (navigator.vibrate) navigator.vibrate(10);
      }

      this.handleBallBrickCollisions(ball);
      this.applyVortexEffects(ball);
    }

    this.updateProjectiles();
    this.updatePowerUpCapsules();
    this.updateEMP();
    this.score.update(dtMs);

    // Ball loss check
    const aliveBalls = this.balls.filter(b => b.alive);
    if (aliveBalls.length === 0) {
      this.onAllBallsLost();
    } else {
      this.balls = aliveBalls;
    }

    // Level clear check (all destructible bricks cleared)
    const remaining = this.bricks.filter(
      b => b.alive && b.type !== C.BRICK_INDESTRUCTIBLE
    );
    if (remaining.length === 0) {
      this.onLevelClear();
    }
  }

  updateBossIntro(input, dtMs) {
    this.paddle.update(input);
    if (this.balls.length > 0 && this.balls[0].attached) {
      this.balls[0].attachTo(this.paddle);
    }

    this.bossIntroTimer -= dtMs;
    if (this.bossIntroTimer <= 0) {
      this.state = STATE.BOSS_FIGHT;
    }
  }

  updateBossFight(input, dtMs) {
    if (input.pausePressed) {
      this.state = STATE.PAUSED;
      return;
    }

    this.paddle.update(input);
    this.paddle.w = C.PADDLE_W * this.powerUpManager.paddleWidthMultiplier;
    this.powerUpManager.update(dtMs);

    if (this.laserCooldown > 0) this.laserCooldown--;
    if (this.powerUpManager.laserActive && input.launchPressed && this.laserCooldown <= 0) {
      const anyAttached = this.balls.some(b => b.attached);
      if (!anyAttached) {
        this.projectiles.push(new Projectile(this.paddle.x - 10, this.paddle.top));
        this.projectiles.push(new Projectile(this.paddle.x + 10, this.paddle.top));
        this.laserCooldown = 10;
        this.audio.laserShot();
      }
    }

    // Update Boss logic
    if (this.boss && this.boss.alive) {
      this.boss.update(dtMs, this.balls, this.paddle);

      // Check boss projectile collisions against paddle
      if (this.boss.projectiles) {
        for (const p of this.boss.projectiles) {
          if (!p.alive) continue;
          if (
            p.x + p.w / 2 > this.paddle.left &&
            p.x - p.w / 2 < this.paddle.right &&
            p.y + p.h / 2 > this.paddle.top &&
            p.y - p.h / 2 < this.paddle.bottom
          ) {
            p.alive = false;
            this.camera.shake(5, 120);
            this.particles.emit(p.x, p.y, 8, '#ff4444', { speedMin: 1, speedMax: 3 });
            if (navigator.vibrate) navigator.vibrate(25);
          }
        }
      }
    }

    // Update balls
    let launchedThisFrame = false;
    for (const ball of this.balls) {
      if (ball.attached) {
        ball.attachTo(this.paddle);
        if (input.launchPressed && !launchedThisFrame) {
          ball.launch(this.paddle);
          launchedThisFrame = true;
        }
        continue;
      }

      ball.update();

      if (Physics.ballPaddle(ball, this.paddle)) {
        if (this.powerUpManager.magnetCatches > 0) {
          ball.attachTo(this.paddle);
          this.powerUpManager.useMagnetCatch();
        }
        this.audio.paddleHit((ball.x - this.paddle.x) / (this.paddle.w / 2));
        if (navigator.vibrate) navigator.vibrate(10);
      }

      // Check ball hit on boss
      if (this.boss && this.boss.alive) {
        const hit = this.boss.checkBallHit(ball);
        if (hit) {
          this.audio.bossHit();
          this.camera.shake(4, 80);
          this.particles.emit(ball.x, ball.y, 8, '#ff00ff', { speedMin: 1.5, speedMax: 4 });
          this.score.score += 100;
          if (navigator.vibrate) navigator.vibrate(15);
        }
      }
    }

    // Check laser hit on boss
    if (this.boss && this.boss.alive) {
      for (const proj of this.projectiles) {
        if (!proj.alive) continue;
        // Test projectile vs boss core/weak points
        const hit = this.boss.checkBallHit({ x: proj.x, y: proj.y, radius: 4, vx: 0, vy: -6 });
        if (hit) {
          proj.alive = false;
          this.audio.bossHit();
          this.particles.emit(proj.x, proj.y, 4, '#00ffff');
          this.score.score += 50;
        }
      }
    }

    this.updateProjectiles();
    this.updatePowerUpCapsules();
    this.updateEMP();
    this.score.update(dtMs);

    // Ball loss
    const aliveBalls = this.balls.filter(b => b.alive);
    if (aliveBalls.length === 0) {
      this.onAllBallsLost();
    } else {
      this.balls = aliveBalls;
    }

    // Boss defeat check
    if (this.boss && this.boss.defeated) {
      this.onBossDefeated();
    }
  }

  updateProjectiles() {
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const proj = this.projectiles[i];
      proj.update();
      if (!proj.alive) {
        this.projectiles.splice(i, 1);
        continue;
      }

      for (const brick of this.bricks) {
        if (!brick.collidable) continue;
        if (proj.hitsBrick(brick)) {
          proj.alive = false;
          const destroyed = brick.hit(proj.damage);
          if (destroyed) {
            this.onBrickDestroyed(brick);
          } else {
            this.audio.armoredHit();
            this.particles.emit(brick.centerX, brick.centerY, 4, brick.color);
          }
          break;
        }
      }
    }
  }

  updatePowerUpCapsules() {
    for (let i = this.powerUps.length - 1; i >= 0; i--) {
      const pu = this.powerUps[i];
      pu.update();
      if (!pu.alive) {
        this.powerUps.splice(i, 1);
        continue;
      }
      if (pu.hitsPaddle(this.paddle)) {
        pu.alive = false;
        this.activatePowerUp(pu.type);
      }
    }
  }

  updateEMP() {
    if (this.empWave) {
      this.empWave.radius += 4;
      this.empWave.alpha -= 0.02;
      if (this.empWave.radius >= this.empWave.maxRadius || this.empWave.alpha <= 0) {
        this.empWave = null;
      }
    }
  }

  handleBallBrickCollisions(ball) {
    const isFireball = this.powerUpManager.fireballActive;
    const hits = Physics.ballBricks(ball, this.bricks, isFireball);

    for (const hit of hits) {
      if (hit.destroyed) {
        this.onBrickDestroyed(hit.brick);
        if (isFireball) {
          // Fiery incineration explosion
          this.particles.emit(hit.brick.centerX, hit.brick.centerY, 12, '#ff4400', {
            speedMin: 2, speedMax: 5, sizeMin: 2, sizeMax: 4
          });
          this.camera.shake(C.SHAKE_BRICK.intensity * 1.5, C.SHAKE_BRICK.duration);
        }
      } else {
        this.audio.armoredHit();
        this.camera.shake(C.SHAKE_ARMORED.intensity, C.SHAKE_ARMORED.duration);
        this.particles.emit(hit.brick.centerX, hit.brick.centerY, 4, hit.brick.color);
      }
    }
  }

  onBrickDestroyed(brick) {
    this.particles.emitBrickShatter(brick.x, brick.y, brick.w, brick.h, brick.color);
    this.score.addBrickScore(brick.points, brick.centerX, brick.centerY);
    this.audio.brickDestroy(this.score.combo);
    this.camera.shake(C.SHAKE_BRICK.intensity, C.SHAKE_BRICK.duration);
    if (navigator.vibrate) navigator.vibrate(15);

    if (brick.type === C.BRICK_TNT) this.triggerTNT(brick, 0);
    if (brick.type === C.BRICK_PRISM) this.triggerPrism();
    this.tryDropPowerUp(brick);
  }

  triggerTNT(tntBrick, chainIndex) {
    this.audio.tntExplode();
    this.camera.shake(C.SHAKE_TNT.intensity, C.SHAKE_TNT.duration);

    this.particles.emit(tntBrick.centerX, tntBrick.centerY, 20, '#ff6600', { speedMin: 2, speedMax: 6 });
    this.particles.emit(tntBrick.centerX, tntBrick.centerY, 12, '#ffff00', { speedMin: 1, speedMax: 4 });

    const newTNTs = [];
    for (const brick of this.bricks) {
      if (!brick.alive || brick === tntBrick) continue;

      const colDist = Math.abs(brick.col - tntBrick.col);
      const rowDist = Math.abs(brick.row - tntBrick.row);

      if (colDist <= 1 && rowDist <= 1) {
        const destroyed = brick.hit(2);
        if (destroyed) {
          this.particles.emitBrickShatter(brick.x, brick.y, brick.w, brick.h, brick.color);
          this.score.addBrickScore(brick.points, brick.centerX, brick.centerY);
          if (brick.type === C.BRICK_TNT) newTNTs.push(brick);
        }
      }
    }

    if (chainIndex > 0) {
      this.audio.chainLink(chainIndex);
      this.camera.triggerChromaticAberration(Math.min(10, 4 + chainIndex * 2), 220);
      this.camera.triggerFlash(0.2);
    }
    for (const tnt of newTNTs) this.triggerTNT(tnt, chainIndex + 1);
  }

  triggerPrism() {
    this.audio.prismSplit();
    const currentBalls = this.balls.filter(b => b.alive && !b.attached);
    if (currentBalls.length === 0 || this.balls.length >= 16) return;

    const source = currentBalls[0];
    for (let i = 0; i < 2; i++) {
      if (this.balls.length >= 16) break;
      const nb = new Ball();
      nb.x = source.x;
      nb.y = source.y;
      nb.attached = false;
      nb.alive = true;

      const angle = Math.atan2(source.vy, source.vx) + (i === 0 ? -0.35 : 0.35);
      const speed = Math.sqrt(source.vx * source.vx + source.vy * source.vy);
      nb.vx = Math.cos(angle) * speed;
      nb.vy = Math.sin(angle) * speed;
      this.balls.push(nb);
    }
  }

  applyVortexEffects(ball) {
    if (ball.attached) return;
    for (const brick of this.bricks) {
      if (!brick.alive || brick.type !== C.BRICK_VORTEX) continue;
      const dx = brick.centerX - ball.x;
      const dy = brick.centerY - ball.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 80 && dist > 5) {
        const force = 0.03 / (dist / 40);
        ball.vx += (dx / dist) * force;
        ball.vy += (dy / dist) * force;
      }
    }
  }

  tryDropPowerUp(brick) {
    let dropRate = C.POWERUP_DROP_RATE;
    if (this.score.lives === 2) dropRate = 0.20;
    if (this.score.lives === 1) dropRate = 0.30;
    if (brick.type === C.BRICK_INDESTRUCTIBLE) return;

    if (Math.random() < dropRate) {
      const types = Object.values(PU_TYPES);
      const type = types[Math.floor(Math.random() * types.length)];
      const pu = new PowerUp(brick.centerX, brick.centerY, type);
      this.powerUps.push(pu);
      this.audio.powerUpDrop();
    }
  }

  activatePowerUp(type) {
    this.audio.powerUpCatch();
    if (navigator.vibrate) navigator.vibrate(20);

    const result = this.powerUpManager.activate(type);
    if (!result) return;

    if (result.action === 'multiball') this.triggerMultiBall();
    if (result.action === 'emp') this.triggerEMP();
  }

  triggerMultiBall() {
    const activeBalls = this.balls.filter(b => b.alive && !b.attached);
    if (activeBalls.length === 0) return;

    const newBalls = [];
    for (const ball of activeBalls) {
      if (this.balls.length + newBalls.length >= 16) break;
      for (let i = 0; i < 2; i++) {
        if (this.balls.length + newBalls.length >= 16) break;
        const nb = new Ball();
        nb.x = ball.x;
        nb.y = ball.y;
        nb.attached = false;
        nb.alive = true;

        const angle = Math.atan2(ball.vy, ball.vx) + (i === 0 ? -0.4 : 0.4);
        const speed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
        nb.vx = Math.cos(angle) * speed;
        nb.vy = Math.sin(angle) * speed;
        newBalls.push(nb);
      }
    }
    this.balls.push(...newBalls);
  }

  triggerEMP() {
    this.audio.empBlast();
    this.camera.shake(C.SHAKE_TNT.intensity, C.SHAKE_TNT.duration);

    this.empWave = {
      x: this.paddle.x,
      y: this.paddle.top,
      radius: 10,
      maxRadius: 200,
      alpha: 0.8,
    };

    for (const brick of this.bricks) {
      if (!brick.alive || brick.type === C.BRICK_INDESTRUCTIBLE) continue;
      const dy = this.paddle.top - brick.centerY;
      if (dy >= 0 && dy <= 150) {
        const destroyed = brick.hit(1);
        if (destroyed) this.onBrickDestroyed(brick);
      }
    }
  }

  updatePaused(input) {
    if (input.pausePressed) {
      this.state = (this.boss ? STATE.BOSS_FIGHT : STATE.PLAYING);
      return;
    }

    if (input.tapped) {
      const bx = C.CANVAS_W / 2 - 95;
      const bw = 190;
      const bh = 36;

      // Button 1: Resume Game (y: 260)
      if (input.tapX >= bx && input.tapX <= bx + bw && input.tapY >= 260 && input.tapY <= 260 + bh) {
        this.state = (this.boss ? STATE.BOSS_FIGHT : STATE.PLAYING);
        return;
      }

      // Button 2: Music Toggle (y: 310)
      if (input.tapX >= bx && input.tapX <= bx + bw && input.tapY >= 310 && input.tapY <= 310 + bh) {
        this.audio.toggleMusic();
        return;
      }

      // Button 3: SFX Toggle (y: 360)
      if (input.tapX >= bx && input.tapX <= bx + bw && input.tapY >= 360 && input.tapY <= 360 + bh) {
        this.audio.toggleSFX();
        return;
      }

      // Button 4: Quit to Title (y: 410)
      if (input.tapX >= bx && input.tapX <= bx + bw && input.tapY >= 410 && input.tapY <= 410 + bh) {
        if (this.score.score > 0) {
          const levelName = LEVELS[this.currentLevel % LEVELS.length].name;
          this.leaderboard.submitScore(this.profile.username, this.score.score, `SECTOR ${levelName}`);
        }
        this.state = STATE.TITLE;
        this.audio.playTrack('title');
        return;
      }
    }
  }

  updateLevelClear(dtMs) {
    this.stateTimer -= dtMs;
    if (this.stateTimer <= 0) {
      this.nextLevel();
    }
  }

  updateBossVictory(dtMs) {
    this.stateTimer -= dtMs;
    if (this.stateTimer <= 0) {
      this.nextLevel();
    }
  }

  updateVictory(input) {
    if (input.tapped) {
      if (this.checkShareClick(input.tapX, input.tapY)) {
        return;
      }
      this.state = STATE.TITLE;
      this.audio.playTrack('title');
    }
  }

  updateGameOver(input) {
    if (input.tapped) {
      if (this.checkShareClick(input.tapX, input.tapY)) {
        return;
      }
      this.state = STATE.TITLE;
      this.audio.playTrack('title');
    }
  }

  handleDirectTap(x, y) {
    if (this.state === STATE.VICTORY || this.state === STATE.GAME_OVER) {
      if (this.checkShareClick(x, y)) {
        this.shareScore();
        return true;
      }
    }
    return false;
  }

  checkShareClick(x, y) {
    if (!this.shareBtnBounds) {
      // Fallback area around center bottom
      const bx = C.CANVAS_W / 2 - 95;
      const by = C.CANVAS_H / 2 + 20;
      return x >= bx && x <= bx + 190 && y >= by && y <= by + 50;
    }
    const { x: bx, y: by, w: bw, h: bh } = this.shareBtnBounds;
    // Generous touch buffer padding
    const pad = 6;
    return x >= bx - pad && x <= bx + bw + pad && y >= by - pad && y <= by + bh + pad;
  }

  shareScore() {
    const levelName = LEVELS[this.currentLevel % LEVELS.length].name;
    const pilotName = this.profile ? this.profile.username : 'CYBER-PILOT';
    const shareData = {
      title: 'Bricks-Yo — Turbo Neon Brick Breaker',
      text: `🕹️ Pilot [${pilotName}] scored ${this.score.score.toLocaleString()} in Bricks-Yo (Sector ${levelName})! Can you beat my neon high score?`,
      url: SHARE_URL,
    };

    if (navigator.share) {
      // Synchronously execute within user gesture stack to trigger native OS share sheet
      navigator.share(shareData)
        .then(() => {
          this.toastMessage = 'SHARED SUCCESSFULLY! 🚀';
          this.toastTimer = 2500;
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') {
            // User dismissed/cancelled OS share sheet normally
            return;
          }
          this._fallbackClipboardShare(shareData);
        });
      return;
    }

    this._fallbackClipboardShare(shareData);
  }

  _fallbackClipboardShare(shareData) {
    const fullText = `${shareData.text} Play: ${shareData.url}`;
    let copied = false;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullText).catch(() => {});
      copied = true;
    }

    if (!copied) {
      // DOM Textarea fallback
      try {
        const ta = document.createElement('textarea');
        ta.value = fullText;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        copied = true;
      } catch {}
    }

    this.toastMessage = copied ? 'COPIED TO CLIPBOARD! 🔗' : 'SHARE: ' + shareData.url;
    this.toastTimer = 3000;
  }

  onAllBallsLost() {
    this.camera.shake(C.SHAKE_MISS.intensity, C.SHAKE_MISS.duration);
    this.audio.ballMiss();
    if (navigator.vibrate) navigator.vibrate([30, 50, 30]);

    if (this.powerUpManager.shieldActive) {
      this.powerUpManager.shieldActive = false;
      this.balls = [new Ball()];
      this.balls[0].attachTo(this.paddle);
      return;
    }

    const livesLeft = this.score.loseLife();
    if (livesLeft <= 0) {
      this.audio.gameOver();
      this.state = STATE.GAME_OVER;
      const levelName = LEVELS[this.currentLevel % LEVELS.length].name;
      this.leaderboard.submitScore(this.profile.username, this.score.score, `SECTOR ${levelName}`)
        .then(res => {
          if (res && res.isTop20 && res.rank) {
            this.toastMessage = `★ TOP 20 RECORD: RANK #${res.rank}! ★`;
            this.toastTimer = 4000;
          }
        });
    } else {
      this.balls = [new Ball()];
      this.balls[0].attachTo(this.paddle);
    }
  }

  onLevelClear() {
    this.score.addBonusPoints(1000 + this.score.lives * 500);
    this.audio.levelClear();
    this.camera.triggerSlowMo(0.3, 500);

    this.particles.emit(C.CANVAS_W / 2, C.CANVAS_H / 3, 30, '#ffff00', { speedMin: 2, speedMax: 6 });
    this.particles.emit(C.CANVAS_W / 2, C.CANVAS_H / 3, 20, '#00ffff', { speedMin: 2, speedMax: 5 });

    this.state = STATE.LEVEL_CLEAR;
    this.stateTimer = 2000;
  }

  onBossDefeated() {
    this.score.addBonusPoints(5000 + this.score.lives * 1000);
    this.audio.levelClear();
    this.camera.triggerSlowMo(0.2, 800);
    this.camera.shake(12, 500);

    this.particles.emit(C.CANVAS_W / 2, 130, 60, '#ff00ff', { speedMin: 3, speedMax: 8, lifeMin: 40, lifeMax: 80 });
    this.particles.emit(C.CANVAS_W / 2, 130, 40, '#00ffff', { speedMin: 2, speedMax: 6, lifeMin: 30, lifeMax: 60 });

    this.state = STATE.BOSS_VICTORY;
    this.stateTimer = 3000;
  }

  // --- Render ---

  render(ctx) {
    ctx.fillStyle = C.BG_COLOR;
    ctx.fillRect(0, 0, C.CANVAS_W, C.CANVAS_H);

    this.camera.applyTransform(ctx);

    if (this.state !== STATE.TITLE) {
      this.renderBackground(ctx);
    }

    switch (this.state) {
      case STATE.TITLE:
        this.renderTitle(ctx);
        break;
      case STATE.PLAYING:
      case STATE.PAUSED:
      case STATE.LEVEL_CLEAR:
      case STATE.BOSS_INTRO:
      case STATE.BOSS_FIGHT:
      case STATE.BOSS_VICTORY:
        this.renderGameplay(ctx);
        break;
      case STATE.VICTORY:
        this.renderGameplay(ctx);
        this.renderVictory(ctx);
        break;
      case STATE.GAME_OVER:
        this.renderGameplay(ctx);
        this.renderGameOver(ctx);
        break;
    }

    if (this.state !== STATE.TITLE) {
      this.renderBorders(ctx);
    }

    this.particles.render(ctx);
    this.camera.restoreTransform(ctx);

    if (this.state !== STATE.TITLE) {
      this.score.renderHUD(ctx);
      this.score.renderPopups(ctx);
      this.powerUpManager.renderIndicators(ctx);
      this.powerUpManager.renderShield(ctx);
    }

    if (this.state === STATE.BOSS_INTRO) this.renderBossIntroOverlay(ctx);
    if (this.state === STATE.BOSS_VICTORY) this.renderBossVictoryOverlay(ctx);
    if (this.state === STATE.PAUSED) this.renderPauseOverlay(ctx);
    if (this.state === STATE.LEVEL_CLEAR) this.renderLevelClearOverlay(ctx);

    // Toast message rendering (for share clipboard notification)
    if (this.toastTimer > 0) {
      this.renderToast(ctx);
    }

    // Info Modal (Power-Ups Guide & Privacy/Safety)
    if (this.showInfoModal) {
      this.renderInfoModal(ctx);
    }
  }

  renderBackground(ctx) {
    const lvl = this.currentLevel % LEVELS.length;
    const levelData = LEVELS[lvl];
    const theme = C.SECTOR_THEMES[levelData.sector] || C.SECTOR_THEMES[1];
    const t = this.bgTime;

    // 1. Deep Atmospheric Gradient Base
    const grad = ctx.createLinearGradient(0, 0, 0, C.CANVAS_H);
    grad.addColorStop(0, '#060913');
    grad.addColorStop(0.4, theme.secondary);
    grad.addColorStop(0.8, '#03050a');
    grad.addColorStop(1, '#020306');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, C.CANVAS_W, C.CANVAS_H);

    ctx.save();

    // ─────────────────────────────────────────────────────────────────────────
    // 🎨 UNIQUE PROCEDURAL BACKDROP PER LEVEL (1 to 20)
    // ─────────────────────────────────────────────────────────────────────────

    switch (lvl) {
      // ─── SECTOR 1: NEON GRID (Levels 1-1 to 1-5) ───
      case 0: { // 1-1: Classic Synthwave Horizon Grid
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.08)';
        ctx.lineWidth = 1;
        const horizonY = 380;
        for (let x = -100; x <= C.CANVAS_W + 100; x += 35) {
          ctx.beginPath();
          ctx.moveTo(x, C.CANVAS_H);
          ctx.lineTo(C.CANVAS_W / 2 + (x - C.CANVAS_W / 2) * 0.15, horizonY);
          ctx.stroke();
        }
        for (let y = horizonY; y <= C.CANVAS_H; y += 22) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(C.CANVAS_W, y);
          ctx.stroke();
        }
        // Glowing horizon laser
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.35)';
        ctx.shadowColor = '#00ffff';
        ctx.shadowBlur = 10;
        ctx.beginPath();
        ctx.moveTo(0, horizonY);
        ctx.lineTo(C.CANVAS_W, horizonY);
        ctx.stroke();
        break;
      }

      case 1: { // 1-2: Data Highway Vertical Channels
        ctx.strokeStyle = 'rgba(0, 220, 255, 0.06)';
        for (let x = 20; x < C.CANVAS_W; x += 40) {
          ctx.beginPath();
          ctx.moveTo(x, C.HUD_HEIGHT);
          ctx.lineTo(x, C.CANVAS_H);
          ctx.stroke();
          // Animated data packet
          const packetY = ((t * 80 + x * 7) % (C.CANVAS_H - C.HUD_HEIGHT)) + C.HUD_HEIGHT;
          ctx.fillStyle = 'rgba(0, 255, 255, 0.3)';
          ctx.fillRect(x - 1.5, packetY, 3, 10);
        }
        break;
      }

      case 2: { // 1-3: Armored Hex Honeycomb Grid
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.05)';
        ctx.lineWidth = 1;
        const size = 26;
        for (let y = C.HUD_HEIGHT; y < C.CANVAS_H + size; y += size * 1.5) {
          for (let x = 0; x < C.CANVAS_W + size; x += size * 1.732) {
            const cx = x + ((Math.floor(y / (size * 1.5)) % 2) * size * 0.866);
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
              const a = (i * Math.PI) / 3;
              const px = cx + Math.cos(a) * (size * 0.5);
              const py = y + Math.sin(a) * (size * 0.5);
              if (i === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.stroke();
          }
        }
        break;
      }

      case 3: { // 1-4: High-Speed Velocity Vector Lines
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.08)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 15; i++) {
          const vx = (i * 28 + t * 40) % (C.CANVAS_W + 100) - 50;
          const vy = (i * 48 + t * 120) % (C.CANVAS_H - C.HUD_HEIGHT) + C.HUD_HEIGHT;
          ctx.beginPath();
          ctx.moveTo(vx, vy);
          ctx.lineTo(vx - 20, vy + 40);
          ctx.stroke();
        }
        break;
      }

      case 4: { // 1-5: Hex Core Boss Tactical Radar Reticle
        const cx = C.CANVAS_W / 2;
        const cy = 130;
        ctx.strokeStyle = 'rgba(255, 0, 80, 0.15)';
        ctx.lineWidth = 1;
        for (let r = 40; r <= 180; r += 35) {
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        }
        // Rotating radar threat sweep line
        ctx.strokeStyle = 'rgba(255, 0, 80, 0.35)';
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + Math.cos(t * 1.5) * 200, cy + Math.sin(t * 1.5) * 200);
        ctx.stroke();
        break;
      }

      // ─── SECTOR 2: CYBER DEPTHS (Levels 2-1 to 2-5) ───
      case 5: { // 2-1: Bioluminescent Deep Sea Trench
        for (let i = 0; i < 18; i++) {
          const px = (i * 23 + Math.sin(t + i) * 20) % C.CANVAS_W;
          const py = C.CANVAS_H - ((t * 25 + i * 40) % (C.CANVAS_H - C.HUD_HEIGHT));
          const size = 1.5 + (i % 3);
          ctx.fillStyle = i % 2 === 0 ? 'rgba(0, 255, 170, 0.25)' : 'rgba(0, 200, 255, 0.2)';
          ctx.beginPath();
          ctx.arc(px, py, size, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }

      case 6: { // 2-2: Undulating Hydro Wave Ribbons
        ctx.strokeStyle = 'rgba(0, 255, 180, 0.07)';
        ctx.lineWidth = 1.5;
        for (let w = 0; w < 4; w++) {
          ctx.beginPath();
          for (let x = 0; x <= C.CANVAS_W; x += 15) {
            const y = 120 + w * 120 + Math.sin(x * 0.02 + t * 1.5 + w) * 35;
            if (x === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.stroke();
        }
        break;
      }

      case 7: { // 2-3: Emerald PCB Circuit Traces
        ctx.strokeStyle = 'rgba(0, 255, 140, 0.06)';
        ctx.lineWidth = 1.5;
        for (let x = 30; x < C.CANVAS_W; x += 50) {
          ctx.beginPath();
          ctx.moveTo(x, C.HUD_HEIGHT);
          ctx.lineTo(x, C.CANVAS_H * 0.4);
          ctx.lineTo(x + 20, C.CANVAS_H * 0.5);
          ctx.lineTo(x + 20, C.CANVAS_H);
          ctx.stroke();
          // Micro-chip square
          ctx.fillStyle = 'rgba(0, 255, 140, 0.15)';
          ctx.fillRect(x + 16, C.CANVAS_H * 0.5 - 4, 8, 8);
        }
        break;
      }

      case 8: { // 2-4: Concentric Sonar Pulse Waves
        const cx = C.CANVAS_W / 2;
        const cy = C.CANVAS_H / 2;
        for (let i = 0; i < 5; i++) {
          const r = ((t * 40 + i * 60) % 300);
          const alpha = Math.max(0, 0.18 * (1 - r / 300));
          ctx.strokeStyle = `rgba(0, 255, 200, ${alpha})`;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
      }

      case 9: { // 2-5: Kraken Abyssal Vortex Currents
        const cx = C.CANVAS_W / 2;
        const cy = 110;
        ctx.strokeStyle = 'rgba(0, 255, 150, 0.1)';
        ctx.lineWidth = 1.5;
        for (let i = 0; i < 4; i++) {
          ctx.beginPath();
          for (let a = 0; a < Math.PI * 4; a += 0.2) {
            const rad = a * 15 + ((t * 20 + i * 25) % 40);
            const px = cx + Math.cos(a + t * 0.5 + i) * rad;
            const py = cy + Math.sin(a + t * 0.5 + i) * (rad * 0.7);
            if (a === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.stroke();
        }
        break;
      }

      // ─── SECTOR 3: SOLAR FORGE (Levels 3-1 to 3-5) ───
      case 10: { // 3-1: Plasma Furnace Rising Embers
        for (let i = 0; i < 22; i++) {
          const px = (i * 19 + Math.sin(t * 2 + i) * 15) % C.CANVAS_W;
          const py = C.CANVAS_H - ((t * 45 + i * 35) % (C.CANVAS_H - C.HUD_HEIGHT));
          const size = 1.5 + (i % 2.5);
          ctx.fillStyle = i % 2 === 0 ? 'rgba(255, 100, 0, 0.3)' : 'rgba(255, 200, 0, 0.25)';
          ctx.beginPath();
          ctx.arc(px, py, size, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }

      case 11: { // 3-2: Diagonal Molten Lava Channels
        ctx.strokeStyle = 'rgba(255, 80, 0, 0.08)';
        ctx.lineWidth = 2;
        for (let i = -100; i < C.CANVAS_W + 200; i += 60) {
          ctx.beginPath();
          ctx.moveTo(i, C.HUD_HEIGHT);
          ctx.lineTo(i - 120, C.CANVAS_H);
          ctx.stroke();
        }
        break;
      }

      case 12: { // 3-3: Radiant Honeycomb Thermal Shield
        ctx.strokeStyle = 'rgba(255, 120, 0, 0.06)';
        ctx.lineWidth = 1;
        const size = 30;
        for (let y = C.HUD_HEIGHT; y < C.CANVAS_H + size; y += size * 1.5) {
          for (let x = 0; x < C.CANVAS_W + size; x += size * 1.732) {
            const cx = x + ((Math.floor(y / (size * 1.5)) % 2) * size * 0.866);
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
              const a = (i * Math.PI) / 3;
              const px = cx + Math.cos(a) * (size * 0.5);
              const py = y + Math.sin(a) * (size * 0.5);
              if (i === 0) ctx.moveTo(px, py);
              else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.stroke();
          }
        }
        break;
      }

      case 13: { // 3-4: Coronal Solar Flare Rings
        const cx = C.CANVAS_W / 2;
        const cy = 200;
        for (let i = 0; i < 4; i++) {
          const r = ((t * 35 + i * 70) % 260);
          const alpha = Math.max(0, 0.2 * (1 - r / 260));
          ctx.strokeStyle = `rgba(255, 140, 0, ${alpha})`;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
        }
        break;
      }

      case 14: { // 3-5: Meltdown Reactor Hazard Stripes
        ctx.fillStyle = 'rgba(255, 50, 0, 0.04)';
        for (let y = C.HUD_HEIGHT; y < C.CANVAS_H; y += 40) {
          ctx.fillRect(0, y, C.CANVAS_W, 16);
        }
        ctx.strokeStyle = 'rgba(255, 160, 0, 0.15)';
        ctx.strokeRect(40, 70, C.CANVAS_W - 80, 140);
        break;
      }

      // ─── SECTOR 4: VOID NEXUS (Levels 4-1 to 4-5) ───
      case 15: { // 4-1: Cosmic Starfield Nebula Clusters
        for (let i = 0; i < 30; i++) {
          const px = (i * 37 + 15) % C.CANVAS_W;
          const py = (i * 53 + 40) % (C.CANVAS_H - C.HUD_HEIGHT) + C.HUD_HEIGHT;
          const twinkle = 0.3 + Math.sin(t * 3 + i) * 0.3;
          ctx.fillStyle = i % 2 === 0 ? `rgba(200, 100, 255, ${twinkle})` : `rgba(0, 255, 255, ${twinkle})`;
          ctx.fillRect(px, py, (i % 2) + 1, (i % 2) + 1);
        }
        break;
      }

      case 16: { // 4-2: Event Horizon Gravitational Warp Lattice
        const cx = C.CANVAS_W / 2;
        const cy = C.CANVAS_H / 2;
        ctx.strokeStyle = 'rgba(180, 0, 255, 0.08)';
        for (let x = 0; x <= C.CANVAS_W; x += 30) {
          ctx.beginPath();
          for (let y = C.HUD_HEIGHT; y <= C.CANVAS_H; y += 20) {
            const dx = x - cx;
            const dy = y - cy;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const warp = Math.max(0, 40 - dist * 0.2);
            const wx = x - (dx / (dist + 1)) * warp;
            const wy = y - (dy / (dist + 1)) * warp;
            if (y === C.HUD_HEIGHT) ctx.moveTo(wx, wy);
            else ctx.lineTo(wx, wy);
          }
          ctx.stroke();
        }
        break;
      }

      case 17: { // 4-3: Interdimensional Portal Vortex Spirals
        const cx = C.CANVAS_W / 2;
        const cy = C.CANVAS_H / 2 - 40;
        ctx.strokeStyle = 'rgba(220, 0, 255, 0.12)';
        ctx.lineWidth = 1.5;
        for (let arm = 0; arm < 3; arm++) {
          ctx.beginPath();
          for (let a = 0; a < Math.PI * 3; a += 0.15) {
            const rad = a * 20;
            const px = cx + Math.cos(a + (arm * Math.PI * 2) / 3 + t) * rad;
            const py = cy + Math.sin(a + (arm * Math.PI * 2) / 3 + t) * rad;
            if (a === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.stroke();
        }
        break;
      }

      case 18: { // 4-4: Quantum Matrix Reality Glitch Lines
        ctx.strokeStyle = 'rgba(255, 0, 220, 0.07)';
        ctx.lineWidth = 1;
        for (let y = C.HUD_HEIGHT + 30; y < C.CANVAS_H; y += 35) {
          const shift = Math.sin(t * 4 + y) * 25;
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(C.CANVAS_W / 2 + shift, y);
          ctx.lineTo(C.CANVAS_W / 2 + shift + 10, y + 8);
          ctx.lineTo(C.CANVAS_W, y + 8);
          ctx.stroke();
        }
        break;
      }

      case 19: { // 4-5: Final Boss Void Architect Mandala
        const cx = C.CANVAS_W / 2;
        const cy = 120;
        ctx.strokeStyle = 'rgba(255, 0, 255, 0.15)';
        ctx.lineWidth = 1.5;
        const sides = 6;
        for (let poly = 1; poly <= 4; poly++) {
          const r = poly * 40;
          const rot = t * (poly % 2 === 0 ? 0.4 : -0.4);
          ctx.beginPath();
          for (let s = 0; s <= sides; s++) {
            const a = (s * Math.PI * 2) / sides + rot;
            const px = cx + Math.cos(a) * r;
            const py = cy + Math.sin(a) * r;
            if (s === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.stroke();
        }
        break;
      }
    }

    ctx.restore();
  }

  renderBorders(ctx) {
    const levelData = LEVELS[this.currentLevel % LEVELS.length];
    const theme = C.SECTOR_THEMES[levelData.sector] || C.SECTOR_THEMES[1];
    const bw = C.BORDER_WIDTH;

    ctx.shadowColor = theme.accent;
    ctx.shadowBlur = C.BORDER_GLOW;
    ctx.fillStyle = theme.primary;
    ctx.globalAlpha = 0.7;

    ctx.fillRect(0, C.HUD_HEIGHT, bw, C.CANVAS_H - C.HUD_HEIGHT);
    ctx.fillRect(C.CANVAS_W - bw, C.HUD_HEIGHT, bw, C.CANVAS_H - C.HUD_HEIGHT);
    ctx.fillRect(0, C.HUD_HEIGHT, C.CANVAS_W, bw);

    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1;

    ctx.fillStyle = theme.accent;
    ctx.globalAlpha = 0.4;
    ctx.fillRect(bw, C.HUD_HEIGHT + bw, 1, C.CANVAS_H - C.HUD_HEIGHT - bw);
    ctx.fillRect(C.CANVAS_W - bw - 1, C.HUD_HEIGHT + bw, 1, C.CANVAS_H - C.HUD_HEIGHT - bw);
    ctx.fillRect(bw, C.HUD_HEIGHT + bw, C.CANVAS_W - bw * 2, 1);
    ctx.globalAlpha = 1;
  }

  renderTitle(ctx) {
    const t = this.bgTime;

    // Animated Perspective Synthwave Floor Grid on Title Screen
    ctx.strokeStyle = 'rgba(0, 255, 255, 0.07)';
    ctx.lineWidth = 1;
    const horizonY = 460;
    for (let x = -100; x <= C.CANVAS_W + 100; x += 30) {
      ctx.beginPath();
      ctx.moveTo(x, C.CANVAS_H);
      ctx.lineTo(C.CANVAS_W / 2 + (x - C.CANVAS_W / 2) * 0.1, horizonY);
      ctx.stroke();
    }
    for (let y = horizonY; y <= C.CANVAS_H; y += 18) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(C.CANVAS_W, y);
      ctx.stroke();
    }

    // Floating cyber particles
    for (let i = 0; i < 20; i++) {
      const px = (i * 27 + Math.sin(t + i) * 18) % C.CANVAS_W;
      const py = (i * 35 + t * 25) % horizonY;
      ctx.fillStyle = i % 2 === 0 ? 'rgba(0, 255, 255, 0.25)' : 'rgba(255, 0, 255, 0.2)';
      ctx.fillRect(px, py, 1.5, 1.5);
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.font = 'bold 40px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 20;
    ctx.fillText('BRICKS', C.CANVAS_W / 2 - 32, C.CANVAS_H / 2 - 60);

    ctx.fillStyle = '#ff00ff';
    ctx.shadowColor = '#ff00ff';
    ctx.fillText('-YO', C.CANVAS_W / 2 + 82, C.CANVAS_H / 2 - 60);
    ctx.shadowBlur = 0;

    ctx.font = '12px monospace';
    ctx.fillStyle = '#888888';
    ctx.fillText('TURBO NEON BRICK BREAKER', C.CANVAS_W / 2, C.CANVAS_H / 2 - 20);

    const alpha = 0.5 + Math.sin(this.titlePulse) * 0.5;
    ctx.globalAlpha = alpha;
    ctx.font = '16px monospace';
    ctx.fillStyle = '#ffffff';
    ctx.fillText('TAP TO START', C.CANVAS_W / 2, C.CANVAS_H / 2 + 45);
    ctx.globalAlpha = 1;

    if (this.score.highScore > 0) {
      ctx.font = '12px monospace';
      ctx.fillStyle = '#ffaa00';
      ctx.fillText(`HIGH SCORE: ${this.score.highScore.toLocaleString()}`, C.CANVAS_W / 2, C.CANVAS_H / 2 + 80);
    }

    // Pilot Callsign Badge (interactive reroll pill)
    const callsignY = C.CANVAS_H / 2 + (this.score.highScore > 0 ? 116 : 96);
    const isRerollPressed = this.clickedBtn?.id === 'btn_reroll';
    ctx.fillStyle = isRerollPressed ? 'rgba(0, 255, 255, 0.35)' : 'rgba(10, 30, 50, 0.85)';
    ctx.strokeStyle = isRerollPressed ? '#ffffff' : '#00ffff';
    ctx.lineWidth = isRerollPressed ? 2 : 1;
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = isRerollPressed ? 12 : 4;
    ctx.beginPath();
    ctx.roundRect(C.CANVAS_W / 2 - 130, callsignY - 13, 260, 26, 13);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.font = 'bold 11px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.fillText(`⚡ PILOT: ${this.profile.username} 🎲`, C.CANVAS_W / 2, callsignY + (isRerollPressed ? 1 : 0));

    // 100% Local Data Badge
    ctx.font = '10px monospace';
    ctx.fillStyle = '#6688aa';
    ctx.fillText('🔒 100% LOCAL PLAY • ZERO SERVER TRACKING', C.CANVAS_W / 2, C.CANVAS_H / 2 + 142);

    const btnW = 210;
    const btnH = 27;
    const btnX = (C.CANVAS_W - btnW) / 2;

    // Button 1: Power-Ups & Guide
    const btn1Y = 495;
    const isPUPressed = this.clickedBtn?.id === 'btn_powerups';
    ctx.fillStyle = isPUPressed ? 'rgba(0, 255, 255, 0.4)' : 'rgba(15, 30, 55, 0.85)';
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = isPUPressed ? 2 : 1;
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = isPUPressed ? 12 : 0;
    ctx.beginPath();
    ctx.roundRect(btnX + (isPUPressed ? 2 : 0), btn1Y + (isPUPressed ? 1 : 0), btnW - (isPUPressed ? 4 : 0), btnH, 6);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.font = 'bold 10px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.fillText('⚡ POWER-UPS & GUIDE ℹ️', C.CANVAS_W / 2, btn1Y + btnH / 2 + (isPUPressed ? 1 : 0));

    // Button 2: Top 20 Leaderboard
    const btn2Y = 530;
    const isLBPressed = this.clickedBtn?.id === 'btn_leaderboard';
    ctx.fillStyle = isLBPressed ? 'rgba(255, 204, 0, 0.4)' : 'rgba(35, 30, 10, 0.85)';
    ctx.strokeStyle = '#ffcc00';
    ctx.lineWidth = isLBPressed ? 2 : 1;
    ctx.shadowColor = '#ffcc00';
    ctx.shadowBlur = isLBPressed ? 12 : 0;
    ctx.beginPath();
    ctx.roundRect(btnX + (isLBPressed ? 2 : 0), btn2Y + (isLBPressed ? 1 : 0), btnW - (isLBPressed ? 4 : 0), btnH, 6);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.fillStyle = '#ffdd00';
    ctx.fillText('🏆 TOP 20 LEADERBOARD 👑', C.CANVAS_W / 2, btn2Y + btnH / 2 + (isLBPressed ? 1 : 0));

    // Button 3: Sound Settings
    const btn3Y = 565;
    const isSoundPressed = this.clickedBtn?.id === 'btn_sound';
    ctx.fillStyle = isSoundPressed ? 'rgba(0, 255, 170, 0.4)' : 'rgba(20, 25, 45, 0.85)';
    ctx.strokeStyle = '#00ffaa';
    ctx.lineWidth = isSoundPressed ? 2 : 1;
    ctx.shadowColor = '#00ffaa';
    ctx.shadowBlur = isSoundPressed ? 12 : 0;
    ctx.beginPath();
    ctx.roundRect(btnX + (isSoundPressed ? 2 : 0), btn3Y + (isSoundPressed ? 1 : 0), btnW - (isSoundPressed ? 4 : 0), btnH, 6);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.fillStyle = '#00ffaa';
    ctx.fillText('🔊 SOUND OPTIONS ⚙️', C.CANVAS_W / 2, btn3Y + btnH / 2 + (isSoundPressed ? 1 : 0));

    // Button 4: Privacy & Warnings
    const btn4Y = 600;
    const isPrivacyPressed = this.clickedBtn?.id === 'btn_privacy';
    ctx.fillStyle = isPrivacyPressed ? 'rgba(68, 136, 170, 0.4)' : 'rgba(10, 20, 35, 0.85)';
    ctx.strokeStyle = '#4488aa';
    ctx.lineWidth = isPrivacyPressed ? 2 : 1;
    ctx.shadowColor = '#4488aa';
    ctx.shadowBlur = isPrivacyPressed ? 12 : 0;
    ctx.beginPath();
    ctx.roundRect(btnX + (isPrivacyPressed ? 2 : 0), btn4Y + (isPrivacyPressed ? 1 : 0), btnW - (isPrivacyPressed ? 4 : 0), btnH, 6);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.font = '10px monospace';
    ctx.fillStyle = '#88ccee';
    ctx.fillText('🛡️ PRIVACY & WARNINGS ℹ️', C.CANVAS_W / 2, btn4Y + btnH / 2 + (isPrivacyPressed ? 1 : 0));

    // Version & Hit Counter Footer
    ctx.font = '10px monospace';
    ctx.fillStyle = '#445566';
    if (this.loadCount !== null) {
      const counterDigits = String(this.loadCount).padStart(6, '0');
      ctx.fillStyle = '#00ffcc';
      ctx.shadowColor = '#00ffcc';
      ctx.shadowBlur = 4;
      ctx.fillText(`⚡ VISITS: [ ${counterDigits} ]  •  v${VERSION}`, C.CANVAS_W / 2, C.CANVAS_H - 20);
      ctx.shadowBlur = 0;
    } else {
      ctx.fillText(`v${VERSION}`, C.CANVAS_W / 2, C.CANVAS_H - 20);
    }
  }

  renderGameplay(ctx) {
    for (const brick of this.bricks) {
      brick.render(ctx);
    }

    if (this.boss && this.boss.alive) {
      this.boss.render(ctx);
      this.boss.renderHPBar(ctx);
    }

    for (const pu of this.powerUps) {
      pu.render(ctx);
    }

    for (const proj of this.projectiles) {
      proj.render(ctx);
    }

    if (this.paddle) {
      if (this.powerUpManager.fireballActive) {
        ctx.shadowColor = '#ff4400';
        ctx.shadowBlur = 12;
      }
      if (this.powerUpManager.laserActive) {
        ctx.fillStyle = '#4488ff';
        ctx.fillRect(this.paddle.left + 2, this.paddle.top - 4, 3, 4);
        ctx.fillRect(this.paddle.right - 5, this.paddle.top - 4, 3, 4);
      }
      this.paddle.render(ctx);
      ctx.shadowBlur = 0;
    }

    for (const ball of this.balls) {
      if (!ball.alive) continue;
      if (this.powerUpManager.fireballActive && !ball.attached) {
        ctx.shadowColor = '#ff4400';
        ctx.shadowBlur = 15;
        this.particles.emit(ball.x, ball.y, 1, '#ff6600', {
          speedMin: 0.3, speedMax: 1, sizeMin: 1, sizeMax: 2,
          lifeMin: 5, lifeMax: 12, type: 'circle'
        });
      }
      ball.render(ctx, this.powerUpManager.fireballActive);
      ctx.shadowBlur = 0;
    }

    // Aiming guideline preview when ball is attached to paddle
    for (const ball of this.balls) {
      if (!ball.alive || !ball.attached) continue;
      const angle = -Math.PI / 2 + (this.paddle.velocity * 0.03);
      const lineLen = 75;
      const targetX = ball.x + Math.cos(angle) * lineLen;
      const targetY = ball.y + Math.sin(angle) * lineLen;

      ctx.save();
      ctx.strokeStyle = '#00ffff';
      ctx.shadowColor = '#00ffff';
      ctx.shadowBlur = 6;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(ball.x, ball.y);
      ctx.lineTo(targetX, targetY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Glowing aim reticle target pip
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(targetX, targetY, 2.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    if (this.empWave) {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.globalAlpha = this.empWave.alpha;
      ctx.shadowColor = '#88bbff';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(this.empWave.x, this.empWave.y, this.empWave.radius, 0, Math.PI * 2);
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
    }

    const levelData = LEVELS[this.currentLevel % LEVELS.length];
    ctx.font = '10px monospace';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.fillText(levelData.name, C.CANVAS_W - 8, C.HUD_HEIGHT + 8);
  }

  renderBossIntroOverlay(ctx) {
    ctx.fillStyle = 'rgba(255, 0, 50, 0.15)';
    ctx.fillRect(0, C.HUD_HEIGHT, C.CANVAS_W, C.CANVAS_H - C.HUD_HEIGHT);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.font = 'bold 24px monospace';
    ctx.fillStyle = '#ff2255';
    ctx.shadowColor = '#ff0033';
    ctx.shadowBlur = 15;
    ctx.fillText('⚠️ WARNING ⚠️', C.CANVAS_W / 2, C.CANVAS_H / 2 - 40);

    ctx.font = 'bold 20px monospace';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#00ffff';
    ctx.fillText(this.boss ? this.boss.name : 'SECTOR BOSS', C.CANVAS_W / 2, C.CANVAS_H / 2);
    ctx.shadowBlur = 0;

    ctx.font = '12px monospace';
    ctx.fillStyle = '#ffaa00';
    ctx.fillText('PREPARE FOR BATTLE', C.CANVAS_W / 2, C.CANVAS_H / 2 + 40);
  }

  renderBossVictoryOverlay(ctx) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.fillRect(0, 0, C.CANVAS_W, C.CANVAS_H);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.font = 'bold 26px monospace';
    ctx.fillStyle = '#ff00ff';
    ctx.shadowColor = '#ff00ff';
    ctx.shadowBlur = 15;
    ctx.fillText('BOSS SHATTERED!', C.CANVAS_W / 2, C.CANVAS_H / 2 - 30);
    ctx.shadowBlur = 0;

    const bonus = 5000 + this.score.lives * 1000;
    ctx.font = '14px monospace';
    ctx.fillStyle = '#ffff00';
    ctx.fillText(`+${bonus.toLocaleString()} BOSS BONUS`, C.CANVAS_W / 2, C.CANVAS_H / 2 + 10);
  }

  renderPauseOverlay(ctx) {
    ctx.fillStyle = 'rgba(5, 5, 15, 0.88)';
    ctx.fillRect(0, 0, C.CANVAS_W, C.CANVAS_H);

    const pw = 280;
    const ph = 300;
    const px = (C.CANVAS_W - pw) / 2;
    const py = 190;

    // Card frame
    ctx.fillStyle = '#0a101d';
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 2;
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.roundRect(px, py, pw, ph, 8);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Header
    ctx.font = 'bold 22px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.fillText('⏸️ GAME PAUSED', C.CANVAS_W / 2, py + 32);

    const btnW = 210;
    const btnH = 34;
    const bx = (C.CANVAS_W - btnW) / 2;

    // Button 1: Resume Game
    const b1Y = 260;
    ctx.fillStyle = '#00ffff';
    ctx.beginPath();
    ctx.roundRect(bx, b1Y, btnW, btnH, 6);
    ctx.fill();
    ctx.font = 'bold 12px monospace';
    ctx.fillStyle = '#000000';
    ctx.fillText('▶ RESUME GAME', C.CANVAS_W / 2, b1Y + btnH / 2);

    // Button 2: Music Toggle
    const b2Y = 310;
    const musicOn = this.audio.musicEnabled;
    ctx.fillStyle = musicOn ? 'rgba(0, 255, 170, 0.18)' : 'rgba(255, 60, 60, 0.18)';
    ctx.strokeStyle = musicOn ? '#00ffaa' : '#ff4444';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.roundRect(bx, b2Y, btnW, btnH, 6);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = musicOn ? '#00ffaa' : '#ff6666';
    ctx.fillText(`🎵 MUSIC: ${musicOn ? 'ON' : 'OFF'}`, C.CANVAS_W / 2, b2Y + btnH / 2);

    // Button 3: SFX Toggle
    const b3Y = 360;
    const sfxOn = this.audio.sfxEnabled;
    ctx.fillStyle = sfxOn ? 'rgba(0, 255, 170, 0.18)' : 'rgba(255, 60, 60, 0.18)';
    ctx.strokeStyle = sfxOn ? '#00ffaa' : '#ff4444';
    ctx.beginPath();
    ctx.roundRect(bx, b3Y, btnW, btnH, 6);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = sfxOn ? '#00ffaa' : '#ff6666';
    ctx.fillText(`💥 SOUND FX: ${sfxOn ? 'ON' : 'OFF'}`, C.CANVAS_W / 2, b3Y + btnH / 2);

    // Button 4: Quit to Title
    const b4Y = 410;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.strokeStyle = '#6688aa';
    ctx.beginPath();
    ctx.roundRect(bx, b4Y, btnW, btnH, 6);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#88aacc';
    ctx.fillText('🏠 QUIT TO TITLE', C.CANVAS_W / 2, b4Y + btnH / 2);
  }

  renderLevelClearOverlay(ctx) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.fillRect(0, 0, C.CANVAS_W, C.CANVAS_H);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.font = 'bold 28px monospace';
    ctx.fillStyle = '#00ff88';
    ctx.shadowColor = '#00ff88';
    ctx.shadowBlur = 15;
    ctx.fillText('LEVEL CLEAR!', C.CANVAS_W / 2, C.CANVAS_H / 2 - 20);
    ctx.shadowBlur = 0;

    const bonus = 1000 + this.score.lives * 500;
    ctx.font = '14px monospace';
    ctx.fillStyle = '#ffff00';
    ctx.fillText(`+${bonus.toLocaleString()} BONUS`, C.CANVAS_W / 2, C.CANVAS_H / 2 + 15);
  }

  renderVictory(ctx) {
    ctx.fillStyle = 'rgba(10, 0, 25, 0.85)';
    ctx.fillRect(0, 0, C.CANVAS_W, C.CANVAS_H);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.font = 'bold 30px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 20;
    ctx.fillText('VICTORY!', C.CANVAS_W / 2, C.CANVAS_H / 2 - 120);

    ctx.fillStyle = '#ff00ff';
    ctx.shadowColor = '#ff00ff';
    ctx.font = 'bold 16px monospace';
    ctx.fillText('YOU CONQUERED THE NEON VOID', C.CANVAS_W / 2, C.CANVAS_H / 2 - 80);
    ctx.shadowBlur = 0;

    ctx.font = 'bold 13px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.fillText(`PILOT: ${this.profile.username}`, C.CANVAS_W / 2, C.CANVAS_H / 2 - 50);

    ctx.font = '20px monospace';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(`FINAL SCORE: ${this.score.score.toLocaleString()}`, C.CANVAS_W / 2, C.CANVAS_H / 2 - 20);

    this.renderShareButton(ctx, C.CANVAS_H / 2 + 30);

    ctx.font = '14px monospace';
    ctx.fillStyle = '#aaaaaa';
    ctx.fillText('TAP TO PLAY AGAIN', C.CANVAS_W / 2, C.CANVAS_H / 2 + 100);
  }

  renderGameOver(ctx) {
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.fillRect(0, 0, C.CANVAS_W, C.CANVAS_H);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    ctx.font = 'bold 36px monospace';
    ctx.fillStyle = '#ff4444';
    ctx.shadowColor = '#ff4444';
    ctx.shadowBlur = 15;
    ctx.fillText('GAME OVER', C.CANVAS_W / 2, C.CANVAS_H / 2 - 115);
    ctx.shadowBlur = 0;

    const levelName = LEVELS[this.currentLevel % LEVELS.length].name;
    ctx.font = '13px monospace';
    ctx.fillStyle = '#aaaaaa';
    ctx.fillText(`REACHED: SECTOR ${levelName}`, C.CANVAS_W / 2, C.CANVAS_H / 2 - 75);

    ctx.font = 'bold 13px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.fillText(`PILOT: ${this.profile.username}`, C.CANVAS_W / 2, C.CANVAS_H / 2 - 45);

    ctx.font = '18px monospace';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(`SCORE: ${this.score.score.toLocaleString()}`, C.CANVAS_W / 2, C.CANVAS_H / 2 - 15);

    if (this.score.score >= this.score.highScore && this.score.score > 0) {
      ctx.font = '13px monospace';
      ctx.fillStyle = '#ffaa00';
      ctx.shadowColor = '#ffaa00';
      ctx.shadowBlur = 8;
      ctx.fillText('★ NEW HIGH SCORE! ★', C.CANVAS_W / 2, C.CANVAS_H / 2 + 15);
      ctx.shadowBlur = 0;
    }

    this.renderShareButton(ctx, C.CANVAS_H / 2 + 48);

    ctx.font = '14px monospace';
    ctx.fillStyle = '#aaaaaa';
    ctx.fillText('TAP OUTSIDE TO RETRY', C.CANVAS_W / 2, C.CANVAS_H / 2 + 110);
  }

  renderShareButton(ctx, y) {
    const btnW = 180;
    const btnH = 38;
    const btnX = (C.CANVAS_W - btnW) / 2;
    const btnY = y;
    this.shareBtnBounds = { x: btnX, y: btnY, w: btnW, h: btnH };

    // Glowing pill button
    ctx.fillStyle = '#112244';
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 2;
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.roundRect(btnX, btnY, btnW, btnH, 8);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    ctx.font = 'bold 13px monospace';
    ctx.fillStyle = '#00ffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SHARE SCORE 🔗', C.CANVAS_W / 2, btnY + btnH / 2);
  }

  renderToast(ctx) {
    const toastW = 220;
    const toastH = 32;
    const toastX = (C.CANVAS_W - toastW) / 2;
    const toastY = C.CANVAS_H - 70;

    ctx.fillStyle = 'rgba(0, 255, 150, 0.9)';
    ctx.beginPath();
    ctx.roundRect(toastX, toastY, toastW, toastH, 6);
    ctx.fill();

    ctx.font = 'bold 12px monospace';
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.toastMessage, C.CANVAS_W / 2, toastY + toastH / 2);
  }

  renderInfoModal(ctx) {
    // Backdrop
    ctx.fillStyle = 'rgba(5, 5, 15, 0.95)';
    ctx.fillRect(0, 0, C.CANVAS_W, C.CANVAS_H);

    const mw = 370;
    const mh = 570;
    const mx = (C.CANVAS_W - mw) / 2;
    const my = (C.CANVAS_H - mh) / 2;

    // Card frame
    ctx.fillStyle = '#0a101d';
    ctx.strokeStyle = '#00ffff';
    ctx.lineWidth = 2;
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = 14;
    ctx.beginPath();
    ctx.roundRect(mx, my, mw, mh, 10);
    ctx.fill();
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Tabs Header (4 Tabs)
    const tabW = 82;
    const tabH = 32;
    const tabY = my + 14;

    // Tab 1: Power-Ups / Guide
    const isTab1 = this.modalTab === 'powerups';
    const isTab1Pressed = this.clickedBtn?.id === 'tab1';
    ctx.fillStyle = isTab1 ? '#00ffff' : (isTab1Pressed ? 'rgba(0, 255, 255, 0.4)' : 'rgba(0, 255, 255, 0.15)');
    ctx.beginPath();
    ctx.roundRect(mx + 8, tabY + (isTab1Pressed ? 1 : 0), tabW, tabH, 6);
    ctx.fill();

    ctx.font = 'bold 10px monospace';
    ctx.fillStyle = isTab1 ? '#000000' : '#00ffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('⚡ GUIDE', mx + 8 + tabW / 2, tabY + tabH / 2 + (isTab1Pressed ? 1 : 0));

    // Tab 2: Top 20 Leaderboard
    const isTab2 = this.modalTab === 'leaderboard';
    const isTab2Pressed = this.clickedBtn?.id === 'tab2';
    ctx.fillStyle = isTab2 ? '#ffcc00' : (isTab2Pressed ? 'rgba(255, 204, 0, 0.4)' : 'rgba(255, 204, 0, 0.15)');
    ctx.beginPath();
    ctx.roundRect(mx + 96, tabY + (isTab2Pressed ? 1 : 0), tabW, tabH, 6);
    ctx.fill();

    ctx.fillStyle = isTab2 ? '#000000' : '#ffdd00';
    ctx.fillText('🏆 TOP 20', mx + 96 + tabW / 2, tabY + tabH / 2 + (isTab2Pressed ? 1 : 0));

    // Tab 3: Sound Settings
    const isTab3 = this.modalTab === 'sound';
    const isTab3Pressed = this.clickedBtn?.id === 'tab3';
    ctx.fillStyle = isTab3 ? '#00ffaa' : (isTab3Pressed ? 'rgba(0, 255, 170, 0.4)' : 'rgba(0, 255, 170, 0.15)');
    ctx.beginPath();
    ctx.roundRect(mx + 184, tabY + (isTab3Pressed ? 1 : 0), tabW, tabH, 6);
    ctx.fill();

    ctx.fillStyle = isTab3 ? '#000000' : '#00ffaa';
    ctx.fillText('🔊 SOUND', mx + 184 + tabW / 2, tabY + tabH / 2 + (isTab3Pressed ? 1 : 0));

    // Tab 4: Privacy & Safety
    const isTab4 = this.modalTab === 'privacy';
    const isTab4Pressed = this.clickedBtn?.id === 'tab4';
    ctx.fillStyle = isTab4 ? '#ff88aa' : (isTab4Pressed ? 'rgba(255, 136, 170, 0.4)' : 'rgba(255, 136, 170, 0.15)');
    ctx.beginPath();
    ctx.roundRect(mx + 272, tabY + (isTab4Pressed ? 1 : 0), tabW, tabH, 6);
    ctx.fill();

    ctx.fillStyle = isTab4 ? '#000000' : '#ff88aa';
    ctx.fillText('🛡️ PRIVACY', mx + 272 + tabW / 2, tabY + tabH / 2 + (isTab4Pressed ? 1 : 0));

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(mx + 8, my + 56);
    ctx.lineTo(mx + mw - 8, my + 56);
    ctx.stroke();

    ctx.textAlign = 'left';

    // ─────────────────────────────────────────────────────────────────────────
    // TAB 1: POWER-UPS & BRICKS GUIDE
    // ─────────────────────────────────────────────────────────────────────────
    if (this.modalTab === 'powerups') {
      ctx.font = 'bold 12px monospace';
      ctx.fillStyle = '#00ffff';
      ctx.fillText('🎁 8 POWER-UP CAPSULES', mx + 16, my + 72);

      const items = [
        { icon: 'x3', color: '#ffff00', name: 'Multi-Ball', desc: 'Triples all active balls (up to 16).' },
        { icon: '🔥', color: '#ff4400', name: 'Fireball', desc: '8s of piercing fire through bricks.' },
        { icon: '↔', color: '#00ff44', name: 'Wide Paddle', desc: '12s of 1.6× expanded paddle width.' },
        { icon: '⚡', color: '#4488ff', name: 'Laser Cannon', desc: '10s twin blasters. Tap to shoot.' },
        { icon: '🧲', color: '#cc44ff', name: 'Magnet Catch', desc: 'Catches ball for 3 re-aim launches.' },
        { icon: '💥', color: '#ffffff', name: 'EMP Blast', desc: 'Shockwave damages nearby brick rows.' },
        { icon: '🛡', color: '#ff8800', name: 'Shield Barrier', desc: 'Bottom laser net saves 1 lost ball.' },
        { icon: '▼', color: '#44ddff', name: 'Slow Down', desc: '10s 0.7× speed for precision tracking.' },
      ];

      let py = my + 92;
      for (const item of items) {
        // Pill tag
        ctx.fillStyle = item.color;
        ctx.beginPath();
        ctx.roundRect(mx + 16, py - 10, 24, 15, 3);
        ctx.fill();

        ctx.font = 'bold 9px monospace';
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(item.icon, mx + 28, py - 2);

        ctx.textAlign = 'left';
        ctx.font = 'bold 11px monospace';
        ctx.fillStyle = item.color;
        ctx.fillText(item.name + ':', mx + 46, py - 2);

        ctx.font = '10px monospace';
        ctx.fillStyle = '#bbccdd';
        ctx.fillText(item.desc, mx + 130, py - 2);

        py += 24;
      }

      // Special bricks section
      ctx.font = 'bold 12px monospace';
      ctx.fillStyle = '#ffaa00';
      ctx.fillText('🧱 SPECIAL BRICK TACTICS', mx + 16, py + 10);

      const brickTips = [
        '• TNT (!): Detonates 3×3 grid radius in huge chains.',
        '• Vortex (🌀): Gravitational well curves ball trajectory.',
        '• Prism (▲): Rainbow brick splits ball into 3 on break.',
        '• Regen (+): Recovers HP every 4s if not cleared fast.',
        '• Phase (👻): Cycles solid & ghost. Pass through when ghosted.',
      ];

      let by = py + 28;
      ctx.font = '10px monospace';
      ctx.fillStyle = '#cccccc';
      for (const tip of brickTips) {
        ctx.fillText(tip, mx + 16, by);
        by += 16;
      }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // TAB 2: TOP 20 GLOBAL LEADERBOARD (REAL PLAYERS ONLY)
    // ─────────────────────────────────────────────────────────────────────────
    if (this.modalTab === 'leaderboard') {
      ctx.font = 'bold 12px monospace';
      ctx.fillStyle = '#ffcc00';
      ctx.fillText('🏆 TOP 20 GLOBAL PILOT HALL OF FAME', mx + 16, my + 74);

      const scores = Array.isArray(this.leaderboard.scores) ? this.leaderboard.scores.slice(0, 20) : [];

      if (scores.length === 0) {
        // Empty State: No fake scores, real players only!
        const emptyBoxY = my + 105;
        const emptyBoxH = 260;

        ctx.fillStyle = 'rgba(0, 30, 50, 0.4)';
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(mx + 16, emptyBoxY, mw - 32, emptyBoxH, 8);
        ctx.fill();
        ctx.stroke();

        ctx.textAlign = 'center';
        ctx.font = 'bold 14px monospace';
        ctx.fillStyle = '#00ffff';
        ctx.shadowColor = '#00ffff';
        ctx.shadowBlur = 8;
        ctx.fillText('⚡ NO PILOT RECORDS LOGGED YET ⚡', C.CANVAS_W / 2, emptyBoxY + 50);
        ctx.shadowBlur = 0;

        ctx.font = '11px monospace';
        ctx.fillStyle = '#ccddee';
        ctx.fillText('Play a game & conquer sectors to claim', C.CANVAS_W / 2, emptyBoxY + 95);
        ctx.fillText('RANK #01 in the Global Hall of Fame!', C.CANVAS_W / 2, emptyBoxY + 115);

        ctx.font = 'bold 12px monospace';
        ctx.fillStyle = '#ffcc00';
        ctx.fillText(`YOUR PILOT: [ ${this.profile.username} ]`, C.CANVAS_W / 2, emptyBoxY + 165);

        if (this.score.highScore > 0) {
          ctx.font = '11px monospace';
          ctx.fillStyle = '#00ffaa';
          ctx.fillText(`CURRENT LOCAL BEST: ${this.score.highScore.toLocaleString()} PTS`, C.CANVAS_W / 2, emptyBoxY + 195);
        }

        ctx.textAlign = 'left';
      } else {
        // Table Header
        ctx.font = 'bold 9px monospace';
        ctx.fillStyle = '#6688aa';
        ctx.fillText('RANK', mx + 16, my + 92);
        ctx.fillText('PILOT CALLSIGN', mx + 62, my + 92);
        ctx.textAlign = 'right';
        ctx.fillText('SCORE', mx + 262, my + 92);
        ctx.fillText('SECTOR', mx + mw - 16, my + 92);
        ctx.textAlign = 'left';

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.beginPath();
        ctx.moveTo(mx + 14, my + 97);
        ctx.lineTo(mx + mw - 14, my + 97);
        ctx.stroke();

        let ry = my + 112;
        const totalRows = Math.min(20, Math.max(scores.length, 5));
        for (let i = 0; i < totalRows; i++) {
          const entry = scores[i];

          if (entry) {
            const isMe = (this.profile && entry.name === this.profile.username);

            if (isMe) {
              ctx.fillStyle = 'rgba(0, 255, 255, 0.18)';
              ctx.beginPath();
              ctx.roundRect(mx + 12, ry - 11, mw - 24, 18, 3);
              ctx.fill();
            }

            // Rank Medal
            let rankText = `#${String(entry.rank || (i + 1)).padStart(2, '0')}`;
            let rankColor = '#88aacc';
            if (i === 0) { rankText = '🥇 #01'; rankColor = '#ffdd00'; }
            else if (i === 1) { rankText = '🥈 #02'; rankColor = '#e0f0ff'; }
            else if (i === 2) { rankText = '🥉 #03'; rankColor = '#ffaa66'; }

            ctx.font = 'bold 10px monospace';
            ctx.fillStyle = rankColor;
            ctx.fillText(rankText, mx + 16, ry);

            // Name
            ctx.font = isMe ? 'bold 10px monospace' : '10px monospace';
            ctx.fillStyle = isMe ? '#00ffff' : '#ddeeff';
            ctx.fillText(isMe ? `⚡ ${entry.name}` : entry.name, mx + 62, ry);

            // Score
            ctx.textAlign = 'right';
            ctx.font = 'bold 10px monospace';
            ctx.fillStyle = isMe ? '#ffff00' : '#ffea00';
            ctx.fillText(Number(entry.score).toLocaleString(), mx + 262, ry);

            // Sector
            ctx.font = '9px monospace';
            ctx.fillStyle = '#66aacc';
            ctx.fillText(entry.sector || 'SECTOR 1', mx + mw - 16, ry);
            ctx.textAlign = 'left';
          } else {
            // Open Slot
            ctx.font = '9px monospace';
            ctx.fillStyle = '#334455';
            ctx.fillText(`#${String(i + 1).padStart(2, '0')}`, mx + 16, ry);
            ctx.fillText('--- [ OPEN PILOT SLOT ] ---', mx + 62, ry);
          }

          ry += 19;
        }
      }

      // Refresh Button
      const rfW = 190;
      const rfH = 26;
      const rfX = (C.CANVAS_W - rfW) / 2;
      const rfY = my + mh - 92;
      const isRfPressed = this.clickedBtn?.id === 'btn_refresh';

      ctx.fillStyle = isRfPressed ? 'rgba(0, 255, 255, 0.4)' : 'rgba(0, 255, 255, 0.12)';
      ctx.strokeStyle = '#00ffff';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(rfX, rfY + (isRfPressed ? 1 : 0), rfW, rfH, 4);
      ctx.fill();
      ctx.stroke();

      ctx.font = 'bold 10px monospace';
      ctx.fillStyle = '#00ffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🔄 REFRESH TOP 20', C.CANVAS_W / 2, rfY + rfH / 2 + (isRfPressed ? 1 : 0));
      ctx.textAlign = 'left';
    }

    // ─────────────────────────────────────────────────────────────────────────
    // TAB 2: SOUND & MUSIC OPTIONS
    // ─────────────────────────────────────────────────────────────────────────
    if (this.modalTab === 'sound') {
      ctx.font = 'bold 13px monospace';
      ctx.fillStyle = '#00ffaa';
      ctx.fillText('🔊 AUDIO CONTROLS & SETTINGS', mx + 16, my + 80);

      const optBtnW = 310;
      const optBtnH = 44;
      const optBtnX = mx + 30;

      // Music Toggle Button
      const bMusicY = my + 105;
      const musicOn = this.audio.musicEnabled;
      const isMusicPressed = this.clickedBtn?.id === 'opt_music';
      ctx.fillStyle = isMusicPressed ? 'rgba(0, 255, 170, 0.35)' : (musicOn ? 'rgba(0, 255, 170, 0.15)' : 'rgba(255, 60, 60, 0.15)');
      ctx.strokeStyle = musicOn ? '#00ffaa' : '#ff4444';
      ctx.lineWidth = isMusicPressed ? 2.5 : 1.5;
      ctx.beginPath();
      ctx.roundRect(optBtnX, bMusicY + (isMusicPressed ? 1 : 0), optBtnW, optBtnH, 8);
      ctx.fill();
      ctx.stroke();

      ctx.font = 'bold 13px monospace';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('🎵 SYNTH MUSIC:', optBtnX + 16, bMusicY + 26 + (isMusicPressed ? 1 : 0));
      ctx.fillStyle = musicOn ? '#00ffaa' : '#ff5555';
      ctx.textAlign = 'right';
      ctx.fillText(musicOn ? 'ENABLED [ON]' : 'MUTED [OFF]', optBtnX + optBtnW - 16, bMusicY + 26 + (isMusicPressed ? 1 : 0));
      ctx.textAlign = 'left';

      // SFX Toggle Button
      const bSFXY = my + 165;
      const sfxOn = this.audio.sfxEnabled;
      const isSFXPressed = this.clickedBtn?.id === 'opt_sfx';
      ctx.fillStyle = isSFXPressed ? 'rgba(0, 255, 170, 0.35)' : (sfxOn ? 'rgba(0, 255, 170, 0.15)' : 'rgba(255, 60, 60, 0.15)');
      ctx.strokeStyle = sfxOn ? '#00ffaa' : '#ff4444';
      ctx.lineWidth = isSFXPressed ? 2.5 : 1.5;
      ctx.beginPath();
      ctx.roundRect(optBtnX, bSFXY + (isSFXPressed ? 1 : 0), optBtnW, optBtnH, 8);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.fillText('💥 SOUND EFFECTS:', optBtnX + 16, bSFXY + 26 + (isSFXPressed ? 1 : 0));
      ctx.fillStyle = sfxOn ? '#00ffaa' : '#ff5555';
      ctx.textAlign = 'right';
      ctx.fillText(sfxOn ? 'ENABLED [ON]' : 'MUTED [OFF]', optBtnX + optBtnW - 16, bSFXY + 26 + (isSFXPressed ? 1 : 0));
      ctx.textAlign = 'left';

      // Master Mute Button
      const bMuteY = my + 225;
      const muted = this.audio.muted;
      const isMutePressed = this.clickedBtn?.id === 'opt_mute';
      ctx.fillStyle = isMutePressed ? 'rgba(0, 255, 255, 0.35)' : (!muted ? 'rgba(0, 255, 255, 0.12)' : 'rgba(255, 60, 60, 0.2)');
      ctx.strokeStyle = !muted ? '#00ffff' : '#ff4444';
      ctx.lineWidth = isMutePressed ? 2.5 : 1.5;
      ctx.beginPath();
      ctx.roundRect(optBtnX, bMuteY + (isMutePressed ? 1 : 0), optBtnW, optBtnH, 8);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = '#ffffff';
      ctx.fillText('🔇 MASTER AUDIO:', optBtnX + 16, bMuteY + 26 + (isMutePressed ? 1 : 0));
      ctx.fillStyle = !muted ? '#00ffff' : '#ff5555';
      ctx.textAlign = 'right';
      ctx.fillText(!muted ? 'UNMUTED' : 'MUTED ALL', optBtnX + optBtnW - 16, bMuteY + 26 + (isMutePressed ? 1 : 0));
      ctx.textAlign = 'left';

      // Info notes
      ctx.font = '11px monospace';
      ctx.fillStyle = '#99bbdd';
      let infoY = my + 295;
      const soundNotes = [
        '💡 Audio Features:',
        '• Sector Soundtracks: 5 unique synthesized songs.',
        '• Combo Scaling: Dynamic musical note pitch ladders.',
        '• Keyboard Shortcut: Press [ M ] to quickly toggle mute.',
        '• Preference Storage: Settings are automatically saved.',
      ];
      for (const note of soundNotes) {
        ctx.fillText(note, mx + 20, infoY);
        infoY += 19;
      }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // TAB 4: PRIVACY & HEALTH NOTICES
    // ─────────────────────────────────────────────────────────────────────────
    if (this.modalTab === 'privacy') {
      ctx.font = 'bold 12px monospace';
      ctx.fillStyle = '#ffaa00';
      ctx.fillText('1. DATA STORAGE & PRIVACY GUARANTEE', mx + 16, my + 75);

      const privacyLines = [
        `• Pilot Callsign: [ ${this.profile.username} ]`,
        '  The random username is the ONLY identifier saved.',
        '• Zero Behind-the-Scenes Tracking:',
        '  No personal data, IP addresses, emails, accounts,',
        '  or browsing habits are tracked in any way.',
        '• No Cookies or Third-Party Analytics:',
        '  We do not use tracking cookies, ad pixels, or',
        '  cross-site profiling tools of any kind.',
        '• 100% Client-Side Local Play:',
        '  Preferences and high scores are stored strictly',
        '  on your own device via browser localStorage.',
      ];
      let textY = my + 95;
      ctx.font = '10px monospace';
      ctx.fillStyle = '#cccccc';
      for (const line of privacyLines) {
        ctx.fillText(line, mx + 16, textY);
        textY += 17;
      }

      textY += 14;
      ctx.font = 'bold 12px monospace';
      ctx.fillStyle = '#ff4466';
      ctx.fillText('2. HEALTH & SAFETY WARNINGS', mx + 16, textY);

      textY += 22;
      const healthLines = [
        '• Photosensitivity / Seizure Warning:',
        '  Contains rapid flashing neon lights, glow',
        '  explosions, and screenshake effects.',
        '• Audio Volume Notice:',
        '  Synthesized audio contains dynamic sound',
        '  frequencies. Adjust device volume comfortably.',
      ];
      ctx.font = '11px monospace';
      ctx.fillStyle = '#cccccc';
      for (const line of healthLines) {
        ctx.fillText(line, mx + 16, textY);
        textY += 18;
      }
    }

    // Close Button (Prominent & Wide Touchable Area)
    const btnW = 280;
    const btnH = 42;
    const btnX = (C.CANVAS_W - btnW) / 2;
    const btnY = my + mh - 54;
    const isClosePressed = this.clickedBtn?.id === 'btn_close';

    ctx.fillStyle = isClosePressed ? '#ffffff' : '#00ffff';
    ctx.shadowColor = '#00ffff';
    ctx.shadowBlur = isClosePressed ? 20 : 10;
    ctx.beginPath();
    ctx.roundRect(btnX, btnY + (isClosePressed ? 1 : 0), btnW, btnH, 8);
    ctx.fill();
    ctx.shadowBlur = 0;

    ctx.font = 'bold 13px monospace';
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('✕  GOT IT / CLOSE  ✕', C.CANVAS_W / 2, btnY + btnH / 2 + (isClosePressed ? 1 : 0));
  }
}
